/**
 * Profil d'altitude interactif de la fiche d'infos.
 *
 * Zoom de ×1 à ×20 à point focal stable, défilement horizontal une fois zoomé,
 * et marqueurs personnalisés posés sur la courbe — point, trait et symbole,
 * sur deux rangées qui ne se chevauchent pas.
 *
 * Portage d'`ElevationProfileView.kt`. Toute la géométrie qui décide vit dans
 * `elevationProfile.ts`, éprouvée par des tests ; il ne reste ici que le tracé
 * et les gestes, qu'aucun test unitaire n'atteint.
 *
 * Référence : `ElevationProfileView` dans `SPEC.md`, `docs/interface-traces.md`.
 */

import { downsampleProfile } from '../core/elevation'
import { lowerBound } from '../core/geo'
import type { GpxTrack } from '../core/track'
import { canvasPixelRatio } from './canvasScale'
import {
  clampPan,
  clampScale,
  layoutMarkerRows,
  niceStep,
  panForZoom,
  profileMarkers,
  visibleDistanceRange,
  type ProfileMarker,
} from './elevationProfile'
import { MARKER_FONT } from './markerIcons'

/** Hauteur du graphique, en pixels CSS. */
const CHART_HEIGHT = 220

/** Marges de la zone de tracé. Celle du haut dépend des marqueurs. */
const PADDING = { right: 8, bottom: 24, left: 44 } as const

/** Marge haute sans marqueur à loger. */
const PADDING_TOP_BARE = 12

const MARKER_FONT_SIZE = 12

/**
 * Marge haute quand des marqueurs sont posés.
 *
 * Deux rangées de libellés vivent au-dessus de la courbe, comme sur un profil
 * d'organisateur. Sans cette réserve, le symbole de la rangée haute sortirait
 * du canevas et serait rogné sans qu'on comprenne pourquoi.
 */
const PADDING_TOP_MARKERS = 2 * MARKER_FONT_SIZE + 14

/** Un cran de molette vaut ce facteur, choisi pour un zoom progressif. */
const WHEEL_ZOOM_STEP = 1.15

export interface ElevationChart {
  readonly element: HTMLCanvasElement
  /** À appeler à la fermeture : détache les observateurs. */
  destroy(): void
}

/**
 * Construit le graphique et l'accroche à ses gestes.
 *
 * Le canevas est rendu à la densité réelle de l'écran, comme tous les canevas
 * dessinés à la main — voir `canvasScale.ts`.
 */
export function createElevationChart(track: GpxTrack): ElevationChart {
  const canvas = document.createElement('canvas')
  canvas.style.width = '100%'
  canvas.style.height = `${CHART_HEIGHT}px`
  canvas.style.marginTop = '8px'
  canvas.style.display = 'block'
  // Sans quoi le navigateur préempte le geste pour faire défiler le dialogue,
  // et ni le pincement ni le déplacement n'atteignent jamais le canevas.
  canvas.style.touchAction = 'none'

  const distances = track.profileDistances
  // La série brute, comme `ElevationProfileView` : c'est celle dont
  // `minElevation` et `maxElevation` — qui donnent l'échelle et les libellés
  // de grille juste au-dessous — sont les extrêmes. Tracer le lissé les
  // manquait d'un demi-mètre à un mètre sur les traces de référence, et la
  // courbe n'atteignait jamais tout à fait ses lignes extrêmes. Les marqueurs
  // s'interpolent sur la **même** série, sans quoi ils flotteraient au-dessus
  // ou en dessous du trait dans les pentes raides.
  const elevations = track.profileRawElevations

  const markers: ProfileMarker[] =
    distances.length >= 2
      ? profileMarkers(
          track.customMarkers,
          (km) => track.markerKmToRawDistance(km),
          distances,
          elevations,
        )
      : []

  const paddingTop = markers.length > 0 ? PADDING_TOP_MARKERS : PADDING_TOP_BARE

  let scale = 1
  let panOffset = 0

  /** Pointeurs actifs, pour distinguer un déplacement d'un pincement. */
  const pointers = new Map<number, number>()
  let lastPanX = 0
  let pinchDistance = 0

  const separator = (1.1).toLocaleString().charAt(1)

  function draw(): void {
    const ratio = canvasPixelRatio()
    const width = canvas.clientWidth
    const height = CHART_HEIGHT
    if (width <= 0) return

    canvas.width = Math.round(width * ratio)
    canvas.height = Math.round(height * ratio)

    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
    ctx.clearRect(0, 0, width, height)

    if (distances.length < 2) return

    const plotWidth = width - PADDING.left - PADDING.right
    const plotHeight = height - paddingTop - PADDING.bottom
    if (plotWidth <= 0 || plotHeight <= 0) return

    panOffset = clampPan(panOffset, scale, plotWidth)

    const maxDistance = Math.max(distances[distances.length - 1]!, 1)
    const contentWidth = plotWidth * scale
    const minEle = track.minElevation
    const range = Math.max(track.maxElevation - minEle, 10)
    const baselineY = paddingTop + plotHeight

    const distToX = (d: number): number =>
      PADDING.left + panOffset + (d / maxDistance) * contentWidth
    const eleToY = (e: number): number =>
      baselineY - ((e - minEle) / range) * plotHeight

    const visible = visibleDistanceRange(
      panOffset,
      scale,
      plotWidth,
      maxDistance,
    )

    // --- Graduations d'altitude, hors du clip pour déborder sur la marge ---
    ctx.font = '11px system-ui, sans-serif'
    ctx.textAlign = 'right'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = 'rgba(128,128,128,0.9)'
    ctx.strokeStyle = 'rgba(128,128,128,0.35)'
    ctx.lineWidth = 1

    for (let i = 0; i <= 4; i++) {
      const ele = minEle + (range * i) / 4
      const lineY = Math.round(eleToY(ele)) + 0.5
      ctx.fillText(`${Math.round(ele)} m`, PADDING.left - 6, lineY)
    }

    ctx.save()
    ctx.beginPath()
    ctx.rect(PADDING.left, 0, plotWidth, height)
    ctx.clip()

    for (let i = 0; i <= 4; i++) {
      const lineY = Math.round(eleToY(minEle + (range * i) / 4)) + 0.5
      ctx.beginPath()
      ctx.moveTo(PADDING.left, lineY)
      ctx.lineTo(PADDING.left + plotWidth, lineY)
      ctx.stroke()
    }

    // --- Courbe ---
    //
    // Seule la plage visible est parcourue, plus un point de chaque côté pour
    // que le trait touche les bords du clip au lieu de s'arrêter avant. Sans ce
    // fenêtrage, un zoom ×20 relirait les deux cent mille points pour n'en
    // montrer qu'un vingtième.
    const startIndex = Math.max(lowerBound(distances, visible.start) - 1, 0)
    const endIndex = Math.min(
      lowerBound(distances, visible.end) + 1,
      distances.length - 1,
    )

    // La fenêtre déborde légèrement de la plage visible, ses colonnes sont donc
    // un cheveu plus larges qu'un pixel. L'écart est invisible et permet de
    // n'avoir qu'une seule routine de réduction dans tout le portage.
    const reduced = downsampleProfile(
      distances.slice(startIndex, endIndex + 1),
      elevations.slice(startIndex, endIndex + 1),
      Math.max(Math.round(plotWidth), 1),
    )

    if (reduced.distances.length >= 2) {
      const gradient = ctx.createLinearGradient(0, paddingTop, 0, baselineY)
      gradient.addColorStop(0, `${track.color}60`)
      gradient.addColorStop(1, `${track.color}10`)

      ctx.beginPath()
      ctx.moveTo(distToX(reduced.distances[0]!), baselineY)
      for (let i = 0; i < reduced.distances.length; i++) {
        ctx.lineTo(distToX(reduced.distances[i]!), eleToY(reduced.elevations[i]!))
      }
      ctx.lineTo(
        distToX(reduced.distances[reduced.distances.length - 1]!),
        baselineY,
      )
      ctx.closePath()
      ctx.fillStyle = gradient
      ctx.fill()

      ctx.beginPath()
      for (let i = 0; i < reduced.distances.length; i++) {
        const px = distToX(reduced.distances[i]!)
        const py = eleToY(reduced.elevations[i]!)
        if (i === 0) ctx.moveTo(px, py)
        else ctx.lineTo(px, py)
      }
      ctx.strokeStyle = track.color
      ctx.lineWidth = 2
      ctx.stroke()
    }

    // --- Marqueurs, sous le clip : ils suivent le zoom comme la courbe ---
    if (markers.length > 0) {
      ctx.font = `bold ${MARKER_FONT_SIZE}px ${MARKER_FONT}`
      ctx.textAlign = 'left'
      ctx.textBaseline = 'alphabetic'

      // Les marqueurs hors cadre sont écartés avant la répartition : sinon ils
      // consommeraient une rangée qu'aucun libellé visible n'occupe.
      const onScreen = markers
        .map((marker) => ({ marker, x: distToX(marker.distance) }))
        .filter(
          ({ x }) => x >= PADDING.left && x <= PADDING.left + plotWidth,
        )

      const rows = layoutMarkerRows(
        onScreen.map(({ marker, x }) => ({
          x,
          width: ctx.measureText(marker.symbol).width,
        })),
      )

      const lowerBaseline = paddingTop - 6
      const upperBaseline = lowerBaseline - MARKER_FONT_SIZE - 4

      for (const [i, { marker, x }] of onScreen.entries()) {
        const y = eleToY(marker.elevation)
        const row = rows[i]
        const textBaseline =
          row === 'lower'
            ? lowerBaseline
            : row === 'upper'
              ? upperBaseline
              : null

        ctx.strokeStyle = `${track.color}50`
        ctx.lineWidth = 1.5
        ctx.beginPath()
        ctx.moveTo(x, y)
        ctx.lineTo(x, (textBaseline ?? lowerBaseline) + 4)
        ctx.stroke()

        ctx.fillStyle = track.color
        ctx.beginPath()
        ctx.arc(x, y, 3.5, 0, Math.PI * 2)
        ctx.fill()

        if (textBaseline !== null) {
          const labelWidth = ctx.measureText(marker.symbol).width
          ctx.fillText(marker.symbol, x - labelWidth / 2, textBaseline)
        }
      }
    }

    ctx.restore()

    // --- Graduations de distance ---
    ctx.font = '11px system-ui, sans-serif'
    ctx.fillStyle = 'rgba(128,128,128,0.9)'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'top'

    const step = niceStep(maxDistance / 1000 / scale)
    for (
      let km = Math.ceil(visible.start / 1000 / step) * step;
      km * 1000 <= visible.end;
      km += step
    ) {
      const x = distToX(km * 1000)
      if (x < PADDING.left || x > PADDING.left + plotWidth) continue
      const label =
        step >= 1
          ? `${Math.round(km)} km`
          : `${(Math.round(km * 10) / 10).toFixed(1).replace('.', separator)} km`
      ctx.fillText(label, x, baselineY + 6)
    }
  }

  /** Applique un facteur de zoom autour d'une abscisse écran. */
  function zoomAround(clientX: number, factor: number): void {
    const plotWidth = canvas.clientWidth - PADDING.left - PADDING.right
    if (plotWidth <= 0) return

    const previous = scale
    scale = clampScale(scale * factor)
    if (scale === previous) return

    const focusX = clientX - canvas.getBoundingClientRect().left - PADDING.left
    panOffset = clampPan(
      panForZoom(focusX, panOffset, previous, scale),
      scale,
      plotWidth,
    )
    draw()
  }

  canvas.addEventListener('pointerdown', (event) => {
    // La capture garde le doigt sur le canevas même s'il en sort. Elle lève
    // sur un pointeur qui n'est pas réellement actif — un événement synthétisé
    // par une sonde, par exemple —, et l'exception emporterait tout le reste
    // du gestionnaire : le geste ne serait plus jamais suivi.
    try {
      canvas.setPointerCapture(event.pointerId)
    } catch {
      /* pointeur non capturable : le geste reste suivi sans la capture */
    }
    pointers.set(event.pointerId, event.clientX)
    if (pointers.size === 1) {
      lastPanX = event.clientX
    } else if (pointers.size === 2) {
      const [a, b] = [...pointers.values()]
      pinchDistance = Math.abs((a ?? 0) - (b ?? 0))
    }
  })

  canvas.addEventListener('pointermove', (event) => {
    if (!pointers.has(event.pointerId)) return
    pointers.set(event.pointerId, event.clientX)

    if (pointers.size >= 2) {
      const [a, b] = [...pointers.values()]
      const spread = Math.abs((a ?? 0) - (b ?? 0))
      // Un pincement dont les deux doigts se confondent donnerait un facteur
      // infini : on attend qu'ils se séparent.
      if (pinchDistance > 10 && spread > 10) {
        zoomAround(((a ?? 0) + (b ?? 0)) / 2, spread / pinchDistance)
      }
      pinchDistance = spread
      return
    }

    // Le défilement n'existe qu'une fois zoomé : à ×1 les bornes de `clampPan`
    // se confondent, et le geste reste sans effet.
    if (scale > 1) {
      const plotWidth = canvas.clientWidth - PADDING.left - PADDING.right
      panOffset = clampPan(
        panOffset + (event.clientX - lastPanX),
        scale,
        plotWidth,
      )
      draw()
    }
    lastPanX = event.clientX
  })

  const release = (event: PointerEvent): void => {
    pointers.delete(event.pointerId)
    if (pointers.size === 1) {
      // Le doigt restant reprend la main sans saut : sans cette reprise, la
      // courbe bondirait de l'écart accumulé pendant le pincement.
      lastPanX = [...pointers.values()][0] ?? 0
    }
  }
  canvas.addEventListener('pointerup', release)
  canvas.addEventListener('pointercancel', release)

  /*
   * La molette n'a pas d'équivalent Android — il n'y a pas de souris sur un
   * téléphone —, mais sans elle le zoom serait hors d'atteinte sur ordinateur,
   * où l'application tourne aussi. Écart délibéré, noté au `README.md`.
   */
  canvas.addEventListener(
    'wheel',
    (event) => {
      event.preventDefault()
      zoomAround(
        event.clientX,
        event.deltaY < 0 ? WHEEL_ZOOM_STEP : 1 / WHEEL_ZOOM_STEP,
      )
    },
    { passive: false },
  )

  // Le canevas n'a ses dimensions définitives qu'après la mise en page ; le
  // suivi de taille couvre en plus la rotation de l'appareil.
  const observer = new ResizeObserver(() => draw())
  observer.observe(canvas)
  requestAnimationFrame(() => draw())

  return {
    element: canvas,
    destroy: () => observer.disconnect(),
  }
}
